import { ComponentProps, ReactElement, ReactNode, createElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { getMotionProps } from '@/libs'
import { AghanimIcon, ChevronUp, Skeleton, Spinner, cn } from '@dashboard/ui'
import { HubSettingsMenu } from '@/layouts/hub-settings/components'

export const HubSettingsMenuItemSkeleton = () => (
  <motion.li layout>
    <Skeleton className="mx-1.5 my-1 h-6 w-full" />
  </motion.li>
)

type Child = ReactElement<ComponentProps<typeof HubSettingsMenu>>

interface HubSettingsMenuItemProps {
  loading?: boolean
  icon?: AghanimIcon
  prefix?: string
  text: string
  to?: ComponentProps<typeof NavLink>['to']
  onUnfold?: () => void
  children?: Child | false
}

export const HubSettingsMenuItem = ({
  loading = false,
  icon: itemIcon,
  to,
  prefix,
  text,
  onUnfold,
  children,
}: HubSettingsMenuItemProps) => {
  const foldable = !!(children || onUnfold)
  const [isOpen, setIsOpen] = useState(false)
  const onClick = () => {
    !isOpen && onUnfold?.()
    setIsOpen(prev => !prev)
  }
  const icon =
    (loading && <Spinner size={18} />) ||
    (foldable && isOpen && <ChevronUp size={18} className="text-text-brand-primary" />) ||
    (itemIcon && createElement(itemIcon, { size: 18 }))

  return (
    <motion.li layout>
      <div
        className={cn(
          'group/menu-item',
          'block h-fit w-full cursor-default rounded-md px-1.5 py-1 text-text-tertiary',
          '[&:has(a.active)]:bg-fg-primary-alt [&:has(a.active)]:text-text-primary',
        )}
      >
        <ActionItem to={to} onClick={foldable ? onClick : undefined}>
          <div
            className={cn(
              'flex flex-row flex-nowrap items-center gap-1.5 py-[3px]',
              'text-caption-md',
              '[mask-image:linear-gradient(to_right,black_90%,transparent_100%)]',
            )}
          >
            {icon}
            {prefix && <span>{prefix}</span>}
            <span className="overflow-hidden text-nowrap" title={text}>
              {text}
            </span>
          </div>
        </ActionItem>
      </div>

      {foldable && (
        <AnimatePresence initial={false}>
          {!loading && isOpen && <Collapse className=" pl-5">{children || <EmptyMenu />}</Collapse>}
        </AnimatePresence>
      )}
    </motion.li>
  )
}

interface ActionItemProps {
  to?: ComponentProps<typeof NavLink>['to']
  onClick?: () => void
  children?: ReactNode
}

function ActionItem({ to, onClick, ...rest }: ActionItemProps) {
  switch (true) {
    case !!to:
      return (
        <NavLink
          {...rest}
          className="block group-hover/menu-item:text-text-secondary-hover"
          to={to}
          onClick={onClick}
        />
      )
    case !!onClick:
      return (
        <button
          {...rest}
          className="block cursor-pointer group-hover/menu-item:text-text-secondary-hover"
          onClick={onClick}
        />
      )
    default:
      return <div {...rest} className="block cursor-default select-none" />
  }
}

function EmptyMenu() {
  const { t } = useTranslation()

  return (
    <HubSettingsMenu>
      <HubSettingsMenuItem text={t('hub-settings.page-settings.empty')} />
    </HubSettingsMenu>
  )
}

const collapseMotionProps = getMotionProps({
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.2, ease: 'easeOut' },
      opacity: { duration: 0.15, ease: 'easeOut' },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.2, ease: 'easeIn' },
      opacity: { duration: 0.1, ease: 'easeIn' },
    },
  },
})

function Collapse(props: ComponentProps<typeof motion.div>) {
  return <motion.div {...collapseMotionProps} {...props} />
}
